// src/api/itGlueApi.js
// IT Glue documentation API wrappers. All calls go through apiClient (Bearer
// token attached automatically). The IT Glue API key lives server-side only —
// it is NEVER referenced here. Reveal returns a secret value transiently; the
// caller must not persist it (see MspOsMobile IT Glue screens).

import apiClient from "./apiClient";

const ig = {
  // Resolve the IT Glue org id from an Autotask company NAME.
  resolveOrg: (company) =>
    apiClient.get("/api/itglue/org", { params: { company } }).then(r => r.data.orgId),

  // Configurations
  listConfigs: (orgId) =>
    apiClient.get(`/api/itglue/org/${orgId}/configurations`).then(r => r.data.items || []),
  getConfig: (id) =>
    apiClient.get(`/api/itglue/configurations/${id}`).then(r => r.data.item),
  createConfig: (orgId, body) =>
    apiClient.post(`/api/itglue/org/${orgId}/configurations`, body).then(r => r.data.item),
  updateConfig: (id, body) =>
    apiClient.patch(`/api/itglue/configurations/${id}`, body).then(r => r.data.item),
  configRef: () =>
    apiClient.get("/api/itglue/config-types").then(r => r.data), // { types, statuses }
  configExtraRef: () =>
    apiClient.get("/api/itglue/config-ref").then(r => r.data), // { manufacturers, platforms }
  models: (manufacturerId) =>
    apiClient.get(`/api/itglue/manufacturers/${manufacturerId}/models`).then(r => r.data.models || []),
  operatingSystems: (platformId) =>
    apiClient.get(`/api/itglue/platforms/${platformId}/operating-systems`).then(r => r.data.operatingSystems || []),
  orgLocations: (orgId) =>
    apiClient.get(`/api/itglue/org/${orgId}/locations`).then(r => r.data.locations || []),
  orgContacts: (orgId) =>
    apiClient.get(`/api/itglue/org/${orgId}/contacts`).then(r => r.data.contacts || []),

  // Passwords
  listPasswords: (orgId) =>
    apiClient.get(`/api/itglue/org/${orgId}/passwords`).then(r => r.data), // { passwords, folders }
  createPassword: (orgId, body) =>
    apiClient.post(`/api/itglue/org/${orgId}/passwords`, body).then(r => r.data.item),
  revealPassword: (id) =>
    apiClient.get(`/api/itglue/passwords/${id}/reveal`).then(r => r.data.item),

  // Documents
  docFolders: (orgId) =>
    apiClient.get(`/api/itglue/org/${orgId}/document-folders`).then(r => r.data.folders || []),
  createDoc: (orgId, body) =>
    apiClient.post(`/api/itglue/org/${orgId}/documents`, body).then(r => r.data.item),
  // Creates a document with a text body and the photos embedded in its body.
  // body.photos: [{ base64, srcName }] — base64 WITHOUT the data: prefix.
  // Returns { ok, docId, uploaded, failed[], embedFailed }.
  createDocWithPhotos: (orgId, body) =>
    apiClient.post(`/api/itglue/org/${orgId}/documents/with-photos`, body).then(r => r.data),

  // ── Runbook import (IT Glue Upload page) ──────────────────────────────────
  // A four-step flow, and the order is enforced by the backend: stage the
  // extraction, pick the org, DRY RUN, then import. The dry run is mandatory
  // because IT Glue cannot delete a document through the API — an import that
  // goes in wrong is cleaned up by hand in the IT Glue UI or not at all.
  //
  // No password value is ever returned by stage/plan/run. A value crosses to
  // the browser only through importReveal, one at a time, audit-logged like the
  // existing password-reveal route.
  importOrgs: () =>
    apiClient.get("/api/itglue/import/orgs", { timeout: 60000 }).then(r => r.data.organizations || []),
  importConfigTypes: () =>
    apiClient.get("/api/itglue/import/config-types", { timeout: 60000 }).then(r => r.data),
  // Returns { id, meta, rows, warnings } — rows carry no secrets.
  importStage: (exportJson) =>
    apiClient.post("/api/itglue/import/stage", { export: exportJson }, { timeout: 120000 }).then(r => r.data),
  importPlan: (stagingId, orgId, rows) =>
    apiClient.post(`/api/itglue/import/${stagingId}/plan`, { orgId, rows }, { timeout: 120000 }).then(r => r.data),
  // images: { "<file name>": "<base64, no data: prefix>" }
  importRun: (stagingId, orgId, rows, images) =>
    apiClient.post(`/api/itglue/import/${stagingId}/run`, { orgId, rows, images }, { timeout: 600000 }).then(r => r.data),
  importReveal: (stagingId, exportId) =>
    apiClient.get(`/api/itglue/import/${stagingId}/value/${encodeURIComponent(exportId)}`).then(r => r.data.value),
  importDiscard: (stagingId) =>
    apiClient.delete(`/api/itglue/import/${stagingId}`).then(r => r.data),

  // Post-import history. Read-only, server-side, and holds NO password values —
  // names, outcomes and reasons only. It is the only record of what a run
  // deliberately left undone (index-only passwords, unmatched configurations,
  // failures), which is the part nobody can reconstruct afterwards.
  importHistory: (orgId) =>
    apiClient.get("/api/itglue/import/history", { params: orgId ? { orgId } : {} })
      .then(r => r.data.runs || []),
  importHistoryRun: (runId) =>
    apiClient.get(`/api/itglue/import/history/${encodeURIComponent(runId)}`).then(r => r.data.run),
};

export default ig;
